import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREFIX = 'private://';

export const isPrivateFileReference = (value) => (
  typeof value === 'string' && value.startsWith(PREFIX)
);

const getSignedUrl = async (reference) => {
  const fileId = reference.slice(PREFIX.length);
  const response = await axios.get(`${API}/private-files/${fileId}/access`);
  return response.data.url;
};

export const PrivateFileLink = ({ reference, label = 'View / Download', testId, className = '' }) => {
  const [loading, setLoading] = useState(false);

  if (!isPrivateFileReference(reference)) {
    return (
      <a href={reference} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>
        {label}
      </a>
    );
  }

  const openFile = async () => {
    setLoading(true);
    try {
      const url = await getSignedUrl(reference);
      const popup = window.open(url, '_blank', 'noopener,noreferrer');
      if (popup) popup.opener = null;
    } catch {
      toast.error('You are not authorized to access this file.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button type="button" onClick={openFile} disabled={loading} className={className} data-testid={testId}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : label}
    </button>
  );
};

export const PrivateFileImage = ({ reference, alt, className = '', testId }) => {
  const [source, setSource] = useState(isPrivateFileReference(reference) ? null : reference);

  useEffect(() => {
    let active = true;
    if (!isPrivateFileReference(reference)) {
      setSource(reference);
      return undefined;
    }
    getSignedUrl(reference)
      .then((url) => active && setSource(url))
      .catch(() => active && setSource(null));
    return () => { active = false; };
  }, [reference]);

  if (!source) return <div className={`${className} bg-gray-100`} aria-label={alt} data-testid={testId} />;
  return <img src={source} alt={alt} className={className} data-testid={testId} />;
};

export const PrivateFileIconLink = ({ reference, testId }) => (
  <PrivateFileLink
    reference={reference}
    label={<ExternalLink className="w-3.5 h-3.5" />}
    className="text-blue-600 hover:text-blue-700 p-1.5 rounded hover:bg-blue-50"
    testId={testId}
  />
);